import { CalculateStore } from "../../stores/calculateStore";
import { ClientDataType, ClientLegalGroup, ClientModel, LegalFormClientData } from "../../helpers/client";
import { ClientStore } from "../../stores/clientStore";
import { AddressModel } from "../../helpers/address";
import { ProposalStore } from "../../stores/proposalStore";
import { NnwClient } from "../../helpers/nnwClient";
import { UserStore } from "../../stores/userStore";
import { UserRole } from "../../helpers/userRole";
import { UiFlagsStore } from "../../stores/uiFlagsStore";
import { PaymentMethodTypes } from "../../helpers/paymentMethod";

export interface ProposalRequestMapper {
    prepareRequest: (calculationId: string | undefined) => any;
}

export class DefaultProposalRequestMapper implements ProposalRequestMapper {

    constructor(private calculateStore: CalculateStore,
        private clientStore: ClientStore,
        private proposalStore: ProposalStore,
        private userStore: UserStore,
        private uiFlagsStore: UiFlagsStore
    ) {
    }

    public prepareRequest(calculationId: string | undefined) {
        const insurance = {
            ...this.prepareCatAndDog(),
            ...this.prepareNnwInsurance(),
            ...this.prepareGravestone(),
            ...this.prepareSafeSenior(),
            ...this.prepareSecondOpinion()
        };

    return {
      clients: this.prepareClientData(),
      calculationId,
      insurerEmail: this.clientStore.insurer.email?.value,
      insurerPhoneNumber: this.clientStore.insurer.phone?.value,
      paymentMethod: this.mapPaymentMethod(),
      paymentDate: this.proposalStore.paymentToDate,
      requestSource: this.mapRequestSource(),
      issuePlace: this.mapIssuePlace(),
      cession: this.calculateStore.propertyInsurance ? this.prepareCession() : undefined,
      insurance,
      consents: this.prepareConsents(),
      distanceContract: this.mapDistanceContract(),
      ...this.prepareEurofinanceFields(),
      apkUuid: this.calculateStore.apkUuid,
    };
  }

    private prepareClientData() {
        return this.clientStore.clients.map(client => this.mapClient(client));
    }

    private prepareConsents() {
        const consents = this.proposalStore.consents;
        return {
            dataProcessingForMarketing: consents.dataProcessingForMarketing.value,
            thirdPartyMarketing: consents.thirdPartyMarketing.value,
            electronicMeansMarketing: consents.electronicMeansMarketing.value,
            phoneCallMarketing: consents.phoneCallMarketing.value
        };
    }

    private prepareAddressForNnw(client: NnwClient) {
        return (client.addressTheSame && this.calculateStore.propertyInsuranceOrOcChecked)
            ? this.mapAddress(this.calculateStore.estate.address)
            : this.mapAddress(client.address);
    }


    private prepareCession() {
        const cession = this.proposalStore.cession;
        if (cession.cessionSelected) {
            return {
                name: cession.bankName.value,
                nameContinuation: this.returnUndefinedIfEmpty(cession.bankNameContinuation.value),
                zipCode: cession.address.zipCode.value,
                county: cession.address.county.value,
                city: cession.address.city.value,
                street: cession.address.street.value,
                buildingNumber: cession.address.buildingNumber.value,
                apartmentNumber: this.returnUndefinedIfEmpty(cession.address.apartmentNumber.value),
                nip: cession.nip.value,
                regon: cession.regon.value
            };
        } else {
            return undefined;
        }
    }

    private prepareSafeSenior() {
        if (!this.calculateStore.additionalInsurances.safeSenior.checked) {
            return null;
        }
        const seniors: any[] = [];
        this.proposalStore.safeSenior.seniors.forEach(senior => {
            seniors.push({
                firstName: senior.firstName.value,
                lastName: senior.lastName.value,
                pesel: senior.pesel.value,
                address: {
                    zipCode: senior.address.zipCode.value,
                    county: senior.address.county.value,
                    city: senior.address.city.value,
                    street: senior.address.street.value,
                    apartmentNumber: senior.address.apartmentNumber.value,
                    buildingNumber: senior.address.buildingNumber.value
                }
            });
        });

        return {
            safeSenior: {
                clients: seniors
            }
        };
    }

    private prepareNnwInsurance() {
        if (this.proposalStore.nnw.clients.length <= 0 || !this.calculateStore.additionalInsurances.nnwType.checked) {
            return null;
        }

        const clients: {
            heartAttackAndStrokeInsurance: boolean;
            fullName: string;
            pesel: string;
            beneficiary: {
                fullName: string,
                pesel: string,
                address: string;
            } | null
            address: any
        }[] = [];

        this.proposalStore.nnw.clients.forEach((client) => {
            const nnwAddress = this.prepareAddressForNnw(client);
            clients.push({
                heartAttackAndStrokeInsurance: client.heartAttackAndStroke,
                fullName: this.mapNnwFullName(client.firstName.value, client.lastName.value),
                pesel: client.pesel.value,
                beneficiary: (client.beneficiaryChecked)
                    ? {
                        fullName: client.beneficiary.fullName.value,
                        pesel: client.beneficiary.pesel.value,
                        address: client.beneficiary.address.value
                    }
                    : null,
                address: { ...nnwAddress }
            });
        });

        return {
            nnw: {
                clients: (clients)
            }
        };
    }

    private mapNnwFullName(firstName: string | undefined, lastName: string | undefined) {
        if (!firstName && !lastName) {
            return "";
        }
        return `${firstName} ${lastName}`.trim();
    }

    private prepareGravestone() {
        if (!this.calculateStore.additionalInsurances.gravestone.checked) {
            return null;
        }
        const gravestone = this.proposalStore.gravestone;
        return {
            gravestone: {
                cemeteryName: gravestone.cemeteryName.value,
                cemeteryAddress: gravestone.cemeteryAddress.value,
                sector: gravestone.sector.value,
                row: gravestone.row.value,
                plot: gravestone.plot,
                year: gravestone.year.value
            }
        };
    }

    private prepareCatAndDog() {
        if (!this.calculateStore.additionalInsurances.catAndDog.checked) {
            return null;
        }
        const catsAndDogs: any[] = [];
        this.proposalStore.catAndDog.animals.forEach(animal => {
            catsAndDogs.push({
                name: animal.name.value,
                race: animal.race.value,
                age: animal.age.value
            });
        });

        return {
            catAndDog: {
                animals: catsAndDogs
            }
        };
    }

    private prepareSecondOpinion() {
        if (!this.clientStore.insuredInSecondOpinion || !this.calculateStore.displaySecondOpinion) {
            return null;
        }

        return {
            secondOpinion: {
                firstName: this.clientStore.insuredInSecondOpinion.firstName.value,
                lastName: this.clientStore.insuredInSecondOpinion.lastName.value,
                pesel: this.clientStore.insuredInSecondOpinion.pesel.value
            }
        };
    }

    private prepareEurofinanceFields() {
        if (UserRole.isEurofinanceRole(this.userStore.roles)) {
            return {
                authorizedOfwcaEf: this.proposalStore.authorisedOfwca,
                previousInsurerName: this.proposalStore.previousInsurerName,
                clientContactType: this.proposalStore.clientContact
            };
        }
        return undefined;
    }

    private mapClient(client: ClientModel) {
        const companyName = client.companyName?.value;
        const regon = client.regon?.value;
        const nip = client.nip?.value || null;
        const firstName = client.firstName?.value;
        const lastName = client.lastName?.value;
        const pesel = client.pesel?.value;
        const legalForm = client.legalForm.value;
        const insurer = client.insurer;
        const insured = client.insured;
        const shouldCopyEstateAddressToPerson = client.personAddressTheSame && this.calculateStore.propertyInsuranceOrOcChecked;
        const personAddress = shouldCopyEstateAddressToPerson ? this.mapAddress(this.calculateStore.estate.address) : this.mapAddress(client.personAddress);
        const shouldCopyEstateAddressToCompany = client.companyAddressTheSame && this.calculateStore.propertyInsuranceOrOcChecked;
        const companyAddress = shouldCopyEstateAddressToCompany ? this.mapAddress(this.calculateStore.estate.address) : this.mapAddress(client.companyAddress);
        const isPerson = client.legalGroup === ClientLegalGroup.PERSON;

        let contactAddress: any = this.mapAddress(client.contactAddress);
        if (client.contactAddressTheSame) {
            if (isPerson) {
                contactAddress = { ...personAddress };
            } else {
                contactAddress = { ...companyAddress };
            }
        }

        const result = {};

        switch (LegalFormClientData[legalForm!]) {
            case ClientDataType.COMPANY:
                this.addNotEmptyClientData(result, "company", {
                    name: companyName,
                    regon,
                    nip,
                    companyAddress
                });
                break;
            case ClientDataType.PERSON:
                this.addNotEmptyClientData(result, "person", {
                    firstName,
                    lastName,
                    pesel,
                    personAddress
                });
                break;
            case ClientDataType.PERSON_AND_COMPANY:
                this.addNotEmptyClientData(result, "person", {
                    firstName,
                    lastName,
                    pesel
                });
                this.addNotEmptyClientData(result, "company", {
                    name: companyName,
                    regon,
                    nip,
                    companyAddress
                });
                break;
        }

        this.addNotEmptyClientData(result, "insured", insured);
        this.addNotEmptyClientData(result, "insurer", insurer);
        this.addNotEmptyClientData(result, "legalForm", legalForm);
        this.addNotEmptyClientData(result, "contactAddress", contactAddress);

        return result;
    }

    private addNotEmptyClientData(obj: any, field: string, value: any) {
        if ((typeof value !== "object" && value !== undefined) || (typeof value === "object" && Object.keys(value).length)) {
            Object.assign(obj, { [field]: value });
        }
    }

    private returnUndefinedIfEmpty(value: string) {
        return (value && value !== "")
            ? value
            : undefined;
    }

    private mapAddress(address: AddressModel) {

        if (!address) {
            return undefined;
        }
        return {
            apartmentNumber: address.formattedApartmentNumber(),
            buildingNumber: address.buildingNumber.value,
            city: address.city.value,
            county: address.county.value,
            street: address.street.value,
            zipCode: address.zipCode?.value
        };
    }

    private mapPaymentMethod() {
        return this.uiFlagsStore.isClientEndsSelected ? PaymentMethodTypes.PAYU : this.proposalStore.paymentMethod;
    }

    private mapRequestSource(): string {
        return this.isClientClickedRemoteSales() ? "SPRZEDAZZDALNA" : this.getRequestSourceByBirectOrSobol();
    }
    private getRequestSourceByBirectOrSobol(): string {
        return this.uiFlagsStore.isDirect ? "DIRECT" : "SOBOLv2";
    }
    private isClientClickedRemoteSales(): boolean {
        return this.uiFlagsStore.isClientEndsSelected;
    }


    private mapIssuePlace() {
        return (this.uiFlagsStore.isClientEndsSelected || this.uiFlagsStore.isDirect) ? "WARSZAWA" : this.proposalStore.issuePlace.value;
    }

    private mapDistanceContract() {
        return this.uiFlagsStore.isClientEndsSelected ? true : this.proposalStore.distancePolicy.value;
    }
}
